import { atom } from 'jotai';

/**
 * Utility function to create an atom that is synced with the URL search params.
 *
 * @param key - The key to store the value in the URL search params
 * @param initialValue - The initial value to store in the atom
 * @returns An atom that is synced with the URL search params
 */
export const atomWithParams = <T>(key: string, initialValue: T) => {
  const baseAtom = atom<T>(getInitialValue());

  function getInitialValue(): T {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    const params = new URLSearchParams(window.location.search);
    const storedValue = params.get(key);

    if (!storedValue) {
      setParamValue(initialValue);
      return initialValue;
    }

    try {
      // Decode the URL-safe string before parsing
      const decodedValue = decodeURIComponent(storedValue);
      return JSON.parse(decodedValue) as T;
    } catch {
      return initialValue;
    }
  }

  function setParamValue(value: T) {
    try {
      // Encode the stringified value to be URL-safe
      const encodedValue = encodeURIComponent(JSON.stringify(value));

      const newParams = new URLSearchParams(window.location.search);

      if (value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, encodedValue);
      }
      // Check URL length constraints
      const baseUrl = `${window.location.pathname}`;
      const newUrl =
        newParams.size === 0
          ? `${baseUrl}${window.location.hash}`
          : `${baseUrl}?${newParams.toString()}${window.location.hash}`;
      if (newUrl.length > 2000) {
        // Common URL length limit
        console.warn(
          `URL length exceeds 2000 characters for key "${key}". This might cause issues in some browsers.`,
        );
      }

      window.history.replaceState(null, '', newUrl);
    } catch (error) {
      console.error(`Failed to serialize value for key "${key}":`, error);
      // Keep the atom's value but don't update URL if serialization fails
    }
  }

  const derivedAtom = atom(
    (get) => get(baseAtom),
    (_get, set, newValue: T) => {
      set(baseAtom, newValue);
      if (typeof window !== 'undefined') {
        setParamValue(newValue);
      }
    },
  );

  return derivedAtom;
};
